import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setInMemoryToken } from "./api";
import { probeCookieAuth, resetCookieProbe } from "./cookieProbe";

// SESSION-scoped user cache. sessionStorage clears on tab/window close so a
// full re-auth (email + password + MFA) is required on the next visit.
// Never move this to localStorage without an explicit product decision.
const USER_KEY = "sentinel_user";
const readCachedUser = () => {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};
const writeCachedUser = (u) => sessionStorage.setItem(USER_KEY, JSON.stringify(u));
const clearCachedUser = () => sessionStorage.removeItem(USER_KEY);

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(readCachedUser);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      writeCachedUser(r.data);
    } catch (err) {
      // Cookie missing or expired — expected on first load or after logout.
      // Log at debug level so operators can diagnose auth issues without noise.
      if (err?.response?.status && err.response.status !== 401) {
        console.warn("Auth refresh failed:", err.response?.status, err.message);
      }
    }
  }, []);

  useEffect(() => {
    // Whenever we boot the SPA, ask the server if the cookie is still valid.
    // This keeps `user` in sync across tabs/logout and prevents stale caches.
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      // MFA challenge branch — no token yet, return the challenge context up to the caller
      if (r.data?.mfa_required) {
        return { mfa_required: true, mfa_ticket: r.data.mfa_ticket, email: r.data.email };
      }
      // MFA enrollment REQUIRED (first login or after admin reset). No token yet.
      if (r.data?.mfa_enrollment_required) {
        return {
          mfa_enrollment_required: true,
          mfa_enroll_ticket: r.data.mfa_enroll_ticket,
          email: r.data.email,
          name: r.data.name,
        };
      }
      setInMemoryToken(r.data.access_token);
      writeCachedUser(r.data.user);
      setUser(r.data.user);
      probeCookieAuth();
      return r.data.user;
    } finally { setLoading(false); }
  };

  const enrollComplete = async (mfa_enroll_ticket, code) => {
    setLoading(true);
    try {
      const r = await api.post("/auth/mfa/enroll-complete", { mfa_enroll_ticket, code });
      setInMemoryToken(r.data.access_token);
      writeCachedUser(r.data.user);
      setUser(r.data.user);
      probeCookieAuth();
      return r.data; // includes backup_codes
    } finally { setLoading(false); }
  };

  const mfaChallenge = async (mfa_ticket, code) => {
    setLoading(true);
    try {
      const r = await api.post("/auth/mfa/challenge", { mfa_ticket, code });
      setInMemoryToken(r.data.access_token);
      writeCachedUser(r.data.user);
      setUser(r.data.user);
      probeCookieAuth();
      return r.data.user;
    } catch (err) {
      // Server-driven auto-recovery: if the MFA state on the server is missing
      // or corrupt (409), surface a marker so the UI can bounce the user back
      // to email+password (from where they'll be routed to the QR enrollment).
      if (err?.response?.status === 409) {
        err.mfa_reset_required = true;
      }
      throw err;
    } finally { setLoading(false); }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Non-fatal: the cookie is cleared client-side regardless.
      console.warn("Logout API call failed:", err?.message || err);
    }
    setInMemoryToken(null);
    resetCookieProbe();
    clearCachedUser();
    setUser(null);
    window.location.href = "/landing";
  };

  return (
    <AuthCtx.Provider value={{ user, login, mfaChallenge, enrollComplete, logout, loading, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
